import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBusinessPlan } from "../App";
import { useCurrency } from '../contexts/CurrencyContext';
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import TooltipLabel from "./ui/TooltipLabel";
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign, 
  Target, 
  Activity,
  ArrowRight,
  Heart,
  BarChart3,
  FileText,
  LineChart,
  Calendar,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { 
  LineChart as RechartsLineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from "recharts";
import { formatCurrency as formatCurrencyUtil, formatPercentage as formatPercentageUtil } from '../utils/sharedCalculations';
import { usePlanningYears } from '../hooks/usePlanningYears';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PlanningDashboard = () => {
  const navigate = useNavigate();
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({});
  const planningData = usePlanningYears(currentPlan?.id);
  const { years, loading: yearsLoading } = planningData;
  
  // Year selector - default to base year (first year), not last year
  const [selectedYear, setSelectedYear] = useState(null);
  
  // Set default year when years are loaded
  useEffect(() => {
    if (years && years.length > 0 && !selectedYear) {
      setSelectedYear(Math.min(...years)); // Use first year, not last
    }
  }, [years]);

  const formatCurrency = (amount, compact = false) => {
    if (!amount && amount !== 0) return formatCurrencyContext(0, currentPlan?.currency || 'USD', null, compact);
    return formatCurrencyContext(amount, currentPlan?.currency || 'USD', null, compact);
  };

  const formatPercentage = (value) => {
    return formatPercentageUtil(value, 1);
  };

  // Load summary data
  const loadDashboardData = async () => {
    if (!currentPlan?.id || !years || years.length === 0 || !selectedYear) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch selected year financial data
      const financialResponse = await axios.get(
        `${BACKEND_URL}/api/v3/calculations/financial-statements/${currentPlan.id}?year=${selectedYear}`
      );
      
      const financial = financialResponse.data.financial_statements || {};
      const income = financial.income_statement || {};
      const keyMetrics = financial.key_metrics || {};
      
      // Calculate summary metrics
      const revenue = income.revenue || 0;
      const netIncome = income.net_income || 0;
      const netMargin = keyMetrics.net_margin_percent || 0;
      
      // Calculate business health score
      let healthScore = 0;
      if (keyMetrics.gross_margin_percent > 0) healthScore += 25;
      if (keyMetrics.ebitda_margin_percent > 0) healthScore += 25;
      if (netMargin > 0) healthScore += 25;
      if (keyMetrics.operating_ratio < 100) healthScore += 25;
      
      // Fetch multi-year data for chart
      const multiYearPromises = years.map(year =>
        axios.get(`${BACKEND_URL}/api/v3/calculations/financial-statements/${currentPlan.id}?year=${year}`)
          .then(res => ({
            year,
            revenue: res.data.financial_statements?.income_statement?.revenue || 0
          }))
          .catch(() => ({ year, revenue: 0 }))
      );
      
      const multiYearData = await Promise.all(multiYearPromises);
      
      setDashboardData({
        revenue,
        netIncome,
        netMargin,
        healthScore,
        revenueData: multiYearData,
        planningCompletion: 75, // Will be calculated based on actual data
        nextMilestone: "Break-even expected in Month 12"
      });
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [currentPlan?.id, selectedYear, years]);

  if (loading || yearsLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1,2,3,4].map(i => (
              <div key={i} className="h-32 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const { revenue, netIncome, netMargin, healthScore, revenueData, planningCompletion, nextMilestone } = dashboardData;

  // Navigation cards data
  const navigationCards = [
    {
      title: "Financial Health",
      description: "Comprehensive financial health metrics and analysis",
      icon: Heart,
      color: "from-red-50 to-red-100 border-red-200",
      iconColor: "text-red-600",
      route: "/financial-health",
      tooltip: "View detailed financial health metrics including liquidity, profitability, and leverage ratios"
    },
    {
      title: "Performance Analytics",
      description: "Revenue, profitability, and operational performance",
      icon: BarChart3,
      color: "from-blue-50 to-blue-100 border-blue-200",
      iconColor: "text-blue-600",
      route: "/performance-analytics",
      tooltip: "Analyze performance trends, resource allocation, and operational efficiency"
    },
    {
      title: "Planning Details",
      description: "Planning coverage, milestones, and key assumptions",
      icon: FileText,
      color: "from-green-50 to-green-100 border-green-200",
      iconColor: "text-green-600",
      route: "/planning-details",
      tooltip: "Review planning completeness, milestones timeline, and critical assumptions"
    },
    {
      title: "Forecasting & Scenarios",
      description: "Financial forecasts and scenario comparisons",
      icon: LineChart,
      color: "from-purple-50 to-purple-100 border-purple-200",
      iconColor: "text-purple-600",
      route: "/forecasting-scenarios",
      tooltip: "Explore forecasts, scenario analysis, and strategic planning projections"
    }
  ];

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <TooltipLabel
            label={<h1 className="text-3xl font-bold text-slate-900">Planning Dashboard</h1>}
            tooltip="Comprehensive overview of your financial planning with quick access to detailed analytics"
            asChild
          />
          <p className="text-slate-600 mt-1">
            Strategic planning overview for {currentPlan?.company_name || currentPlan?.name}
          </p>
        </div>
        
        {/* Year Selector */}
        {years && years.length > 0 && (
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-slate-700">View Year:</label>
            <select
              value={selectedYear || years[0]}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-4 py-2 border border-slate-300 rounded-lg bg-white text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Hero KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Revenue Card */}
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <TooltipLabel
                  label={<p className="text-sm text-blue-700 font-medium">Annual Revenue ({selectedYear || 'N/A'})</p>}
                  tooltip="Total projected revenue for the selected year from all income sources (product sales, services, subscriptions, etc.). This represents your company's top-line earnings before any expenses. Example: 10,000 units sold at 149,350 ETB each = 1.49B ETB annual revenue. Higher revenue indicates business growth, but must be evaluated alongside profitability."
                />
                <p className="text-2xl font-bold text-blue-900 mt-2">
                  {formatCurrency(revenue, true)}
                </p>
              </div>
              <div className="p-3 bg-blue-200 rounded-full">
                <DollarSign className="h-6 w-6 text-blue-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Net Profit Card */}
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <TooltipLabel
                  label={<p className="text-sm text-green-700 font-medium">Net Profit ({selectedYear || 'N/A'})</p>}
                  tooltip="The bottom-line profit after deducting all operating expenses, interest, taxes, and depreciation from total revenue. Formula: Revenue - COGS - Operating Expenses - Interest - Taxes = Net Profit. Example: 2.016B ETB revenue - 0 ETB expenses = 2.016B ETB net profit. Positive net profit indicates sustainable business; negative means burning cash. Target: Aim for 10-20% of revenue."
                />
                <p className="text-2xl font-bold text-green-900 mt-2">
                  {formatCurrency(netIncome, true)}
                </p>
              </div>
              <div className="p-3 bg-green-200 rounded-full">
                <TrendingUp className="h-6 w-6 text-green-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profit Margin Card */}
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <TooltipLabel
                  label={<p className="text-sm text-purple-700 font-medium">Profit Margin ({selectedYear || 'N/A'})</p>}
                  tooltip="Net profit margin shows how much profit you keep from each dollar of revenue. Formula: (Net Profit / Revenue) × 100. Example: $200K profit ÷ $600K revenue = 33.3% margin. Industry benchmarks: SaaS 20-30%, Retail 5-10%, Consulting 15-25%. Higher margins indicate efficient operations and strong pricing power. Below 5% may signal pricing or cost issues."
                />
                <p className="text-2xl font-bold text-purple-900 mt-2">
                  {formatPercentage(netMargin)}
                </p>
                <div className="mt-2">
                  <div className="w-full bg-purple-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, Math.max(0, netMargin * 2))}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              <div className="p-3 bg-purple-200 rounded-full">
                <Target className="h-6 w-6 text-purple-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Business Health Card */}
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <TooltipLabel
                  label={<p className="text-sm text-orange-700 font-medium">Business Health</p>}
                  tooltip="Composite score (0-100) measuring overall financial health across 4 factors: Gross Margin (25 pts if >0%), EBITDA Margin (25 pts if >0%), Net Margin (25 pts if >0%), Operating Efficiency (25 pts if ratio <100%). Example: All 4 factors positive = 100/100 'Excellent'. Score 80-100: Excellent (strong fundamentals), 60-79: Good (room for improvement), <60: Needs Attention (address cost/revenue issues). View Financial Health Dashboard for detailed breakdown."
                />
                <p className="text-2xl font-bold text-orange-900 mt-2">
                  {healthScore}/100
                </p>
                <div className="mt-2">
                  <Badge 
                    variant={healthScore >= 80 ? "success" : healthScore >= 60 ? "warning" : "destructive"}
                  >
                    {healthScore >= 80 ? "Excellent" : healthScore >= 60 ? "Good" : "Needs Attention"}
                  </Badge>
                </div>
              </div>
              <div className="p-3 bg-orange-200 rounded-full">
                <Activity className="h-6 w-6 text-orange-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Planning Status Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Overall Planning Completion */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <TooltipLabel
              label={
                <CardTitle className="flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                  Planning Completion
                </CardTitle>
              }
              tooltip="Measures how complete your financial plan is across 5 key areas: Revenue Model, Cost Structure, Cash Flow Planning, Break-Even Analysis, and Strategic Initiatives. Each area contributes 20% to overall completion. Example: 4 areas complete (80%) + 1 in progress (15%) = 75% total. Aim for 100% before presenting to investors or lenders. View Planning Details page for area-by-area breakdown."
              asChild
            />
            <CardDescription>Overall progress of your financial planning</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-2xl font-bold text-slate-900">{planningCompletion}%</span>
                <Badge variant={planningCompletion >= 80 ? "success" : "warning"}>
                  {planningCompletion >= 80 ? "Nearly Complete" : "In Progress"}
                </Badge>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-4">
                <div 
                  className="bg-green-500 h-4 rounded-full transition-all duration-500"
                  style={{ width: `${planningCompletion}%` }}
                ></div>
              </div>
              <p className="text-sm text-slate-600">
                Continue building your plan to reach 100% completion
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Next Milestone */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <TooltipLabel
              label={
                <CardTitle className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-blue-600" />
                  Next Milestone
                </CardTitle>
              }
              tooltip="Key planning checkpoints in your business journey: (1) Plan Start - planning begins, (2) Break-Even - when revenue covers all costs, (3) Profitability - first positive net income, (4) Growth Phase - scaling operations, (5) Plan Completion - strategic goals achieved. Break-even is critical: it's when monthly revenue equals monthly expenses. Example: $50K/month costs requires $50K/month revenue to break even. Reaching break-even faster reduces funding needs and risk."
              asChild
            />
            <CardDescription>Upcoming key planning checkpoint</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Target className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Break-Even Point</p>
                  <p className="text-sm text-slate-600 mt-1">{nextMilestone}</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/planning-details')}
                className="w-full"
                variant="outline"
              >
                View All Milestones
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Trend Chart */}
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader>
          <TooltipLabel
            label={
              <CardTitle className="flex items-center">
                <LineChart className="h-5 w-5 mr-2 text-indigo-600" />
                Revenue Trend
              </CardTitle>
            }
            tooltip="5-year revenue projection showing expected growth trajectory. Steady upward trend indicates healthy growth; flat or declining suggests market challenges. Use this to spot: (1) Growth acceleration/deceleration points, (2) Plateau periods requiring new strategies, (3) Revenue targets for each year. Example: $500K Y1 → $1M Y2 → $2M Y3 shows 100% YoY growth. Compare with industry benchmarks: SaaS typically 20-40% YoY, Retail 5-15% YoY. View Forecasting & Scenarios for detailed projections."
            asChild
          />
          <CardDescription>5-year revenue projection</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsLineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="year" stroke="#64748b" />
                <YAxis stroke="#64748b" />
                <Tooltip 
                  formatter={(value) => formatCurrency(value, true)}
                  labelFormatter={(label) => `Year ${label}`}
                />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#3b82f6" 
                  strokeWidth={3}
                  dot={{ fill: '#3b82f6', r: 4 }}
                />
              </RechartsLineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Navigation Cards to Tier 2 Dashboards */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-4">Detailed Analytics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {navigationCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <TooltipLabel
                key={index}
                label={
                  <Card 
                    className={`bg-gradient-to-br ${card.color} hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-1`}
                    onClick={() => navigate(card.route)}
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col items-center text-center space-y-4">
                        <div className={`p-4 bg-white rounded-full shadow-md`}>
                          <Icon className={`h-8 w-8 ${card.iconColor}`} />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-slate-900">{card.title}</h3>
                          <p className="text-sm text-slate-600 mt-2">{card.description}</p>
                        </div>
                        <Button variant="ghost" className="mt-2 w-full">
                          View Details
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                }
                tooltip={card.tooltip}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PlanningDashboard;
